import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, actionKey } from '../../api';
import { useSession } from '../../session';
import type { IndentView, RelSummary } from '../../types';
import { ConfirmDialog, Empty, ErrorBox, Field, Loading, day, dt, useBusy } from '../../ui';
import { useState } from 'react';
import { Icon, Ticket, fuelName, indentTicket, isTodayIst, limitText, plateText, rs } from '../../brand';
import { InviteButton, SentInvitations } from '../shared/Onboarding';

interface BuyerDash {
  suppliers: RelSummary[];
  totals: { availablePaise: string; outstandingPaise: string; reservedPaise: string; overduePaise: string };
  pendingApprovals: number;
  spendThisMonthPaise: string;
  spendTodayPaise: string;
  fillsThisMonth?: number;
  litresThisMonth?: string;
}

/** Dark credit card on company home: what can be used now, and where the rest went. */
export function CreditHero({ s, link }: { s: RelSummary; link?: string }) {
  const p = s.position;
  const limit = BigInt(p.creditLimitPaise);
  const pct = (v: bigint) => (limit > 0n ? Math.min(100, Number((v * 1000n) / limit) / 10) : 0);
  const owedPct = pct(BigInt(p.outstandingPaise));
  const heldPct = Math.min(100 - owedPct, pct(BigInt(p.reservedPaise)));
  const stopped = s.freeze.frozen || s.stoppedForOverdue;
  return (
    <section className="credit-hero" aria-label={`Credit with ${s.counterpartyName}`}>
      <div className="top">
        <span>{link ? <Link to={link}>{s.counterpartyName}</Link> : s.counterpartyName} · can use now</span>
        {stopped ? <span className="hero-pill bad">{s.freeze.frozen ? 'Credit stopped' : 'Overdue – stopped'}</span> : s.status !== 'ACTIVE' ? <span className="hero-pill">{s.status.replace(/_/g, ' ').toLowerCase()}</span> : <span className="hero-pill">Active</span>}
      </div>
      <div className="big">{rs(p.availablePaise)}</div>
      <div className="credit-bar" aria-hidden="true">
        <span className="owed" style={{ width: `${owedPct}%` }} />
        <span className="held" style={{ width: `${heldPct}%` }} />
      </div>
      <div className="credit-legend">
        <span><i style={{ background: '#f2b33d' }} />Owed {rs(p.outstandingPaise)}</span>
        <span><i style={{ background: '#8fd3ad' }} />Approved, not filled {rs(p.reservedPaise)}</span>
        <span>Limit {rs(p.creditLimitPaise)}</span>
      </div>
      {BigInt(p.shortfallPaise) > 0n ? <div className="hero-alert">Over the limit by {rs(p.shortfallPaise)}. Pay to use credit again.</div> : null}
      {s.freeze.frozen && s.freeze.reason ? <div className="hero-alert">Reason: {s.freeze.reason}</div> : null}
      {BigInt(s.pendingReceiptsPaise) > 0n ? <div className="hero-alert">Payment of {rs(s.pendingReceiptsPaise)} waiting for the supplier to confirm.</div> : null}
    </section>
  );
}

function PaymentTile({ suppliers, canPay }: { suppliers: RelSummary[]; canPay: boolean }) {
  const overdue = suppliers.find((s) => BigInt(s.overduePaise) > 0n);
  if (overdue) {
    return (
      <Link className="tile red" to={`/buyer/suppliers/${overdue.relationshipId}`}>
        <span className="t-label">Overdue</span>
        <span className="t-value">{rs(overdue.overduePaise)}</span>
        <span className="t-sub">{overdue.counterpartyName}</span>
        <span className="t-link">{canPay ? 'Pay now' : 'See bill'} →</span>
      </Link>
    );
  }
  const due = suppliers
    .filter((s) => s.nextDueDate && BigInt(s.nextDueAmountPaise) > 0n)
    .sort((a, b) => (a.nextDueDate! < b.nextDueDate! ? -1 : 1))[0];
  if (!due) {
    return (
      <div className="tile">
        <span className="t-label">Bills</span>
        <span className="t-value">{rs('0')}</span>
        <span className="t-sub">Nothing to pay now</span>
      </div>
    );
  }
  return (
    <Link className="tile amber" to={`/buyer/suppliers/${due.relationshipId}`}>
      <span className="t-label">To pay</span>
      <span className="t-value">{rs(due.nextDueAmountPaise)}</span>
      <span className="t-sub">Pay by {day(due.nextDueDate)}</span>
      <span className="t-link">{canPay ? 'Pay now' : 'See bill'} →</span>
    </Link>
  );
}

export function BuyerDashboard() {
  const { can } = useSession();
  const q = useQuery({ queryKey: ['buyer-dash'], queryFn: () => api.get<BuyerDash>('/api/buyer/dashboard') });
  const canSee = can('indent.view.all');
  const recent = useQuery({ queryKey: ['indents', 'recent'], queryFn: () => api.get<IndentView[]>('/api/indents?limit=40'), enabled: canSee, refetchInterval: 30_000 });
  const approve = useApproveFlow();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data!;
  const live = (recent.data ?? []).filter((i) => ['REQUESTED', 'APPROVED', 'CLAIMED'].includes(i.status) || isTodayIst(i.requestedAt));
  const shown = live.slice(0, 6);
  const litresMonth = d.litresThisMonth ? Math.round(Number(d.litresThisMonth)) : 0;
  return (
    <div className="stack">
      <h1 className="sr-only">Company home</h1>
      {!d.suppliers.length ? <Empty>No pumps connected yet. {can('buyer.relationships.manage') ? 'Invite the pump you buy fuel from, or ask its owner to invite you.' : 'Your company owner can invite the pump you buy from.'}</Empty> : null}
      {can('buyer.relationships.manage') ? <div className="row"><InviteButton direction="BUYER_INVITES_SUPPLIER" /></div> : null}
      {can('buyer.relationships.manage') ? <SentInvitations direction="BUYER_INVITES_SUPPLIER" /> : null}
      {d.suppliers.filter((s) => s.status !== 'CLOSED').map((s) => (
        <CreditHero key={s.relationshipId} s={s} link={can('buyer.statements.view') ? `/buyer/suppliers/${s.relationshipId}` : undefined} />
      ))}
      <div className="tiles">
        <PaymentTile suppliers={d.suppliers} canPay={can('buyer.payments.report')} />
        <div className="tile">
          <span className="t-label">Fuel this month</span>
          <span className="t-value">{rs(d.spendThisMonthPaise)}</span>
          <span className="t-sub">{d.fillsThisMonth ?? 0} fill{d.fillsThisMonth === 1 ? '' : 's'} · {litresMonth} L</span>
          <span className="t-sub muted">Today {rs(d.spendTodayPaise)}</span>
        </div>
      </div>
      {canSee ? (
        <>
          <div className="section-head">
            <h2>Today’s indents</h2>
            <Link to="/buyer/indents">See all</Link>
          </div>
          {recent.isLoading ? <Loading /> : <ErrorBox error={recent.error} />}
          {recent.data && !shown.length ? <Empty>No indents today.{can('indent.issue') ? ' Tap “New indent” to make one.' : ''}</Empty> : null}
          <ul className="tickets">
            {shown.map((i) => (
              <Ticket
                key={i.id}
                t={indentTicket(i)}
                to={`/buyer/indents/${i.id}`}
                actions={i.status === 'REQUESTED' && can('indent.approve') ? approve.buttons(i) : undefined}
              />
            ))}
          </ul>
        </>
      ) : null}
      {approve.dialog}
      {can('indent.issue') ? (
        <Link className="fab" to="/buyer/indents/new"><Icon name="plus" size={24} stroke={2.6} />New indent</Link>
      ) : null}
    </div>
  );
}

/** Approve / reject buttons for a waiting indent, with the confirmation dialog. */
export function useApproveFlow() {
  const qc = useQueryClient();
  const [sel, setSel] = useState<IndentView | null>(null);
  const [mode, setMode] = useState<'approve' | 'reject'>('approve');
  const [reason, setReason] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const { busy, error, run, setError } = useBusy();
  const close = () => {
    setSel(null);
    setReason('');
    setExceptionReason('');
    setError(null);
  };
  const act = () =>
    run(async () => {
      if (!sel) return;
      if (mode === 'approve') {
        const k = actionKey(`approve:${sel.id}`);
        await api.post(`/api/indents/${sel.id}/approve`, { capExceptionReason: exceptionReason || undefined }, { idempotencyKey: k.key });
        k.done();
      } else {
        await api.post(`/api/indents/${sel.id}/reject`, { reason });
      }
      await qc.invalidateQueries();
      close();
    });
  const capError = (error as { code?: string } | null)?.code === 'SPEND_CAP_EXCEEDED';
  const buttons = (i: IndentView) => (
    <>
      <button className="btn primary" onClick={() => { setMode('approve'); setSel(i); }}><Icon name="check" size={20} stroke={2.6} />Approve</button>
      <button className="btn" onClick={() => { setMode('reject'); setSel(i); }}>Reject</button>
    </>
  );
  const dialog = (
    <ConfirmDialog
      open={Boolean(sel)}
      title={mode === 'approve' ? 'Approve this indent?' : 'Reject this indent?'}
      lines={sel ? [['Driver', sel.person.name || 'Not specified'], ['Vehicle', sel.vehicle ? plateText(sel.vehicle.registrationNumber) : 'Not specified'], ['Fuel', fuelName(sel.fuelType, sel.category)], ['Limit', limitText(sel)], ['Valid', 'Until end of today']] : []}
      confirmLabel={mode === 'approve' ? 'Approve and get code' : 'Reject'}
      danger={mode === 'reject'}
      busy={busy}
      error={error}
      onConfirm={() => void act()}
      onClose={close}
    >
      {mode === 'reject' ? (
        <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      ) : capError || exceptionReason ? (
        <Field label="One-time exception reason" hint="This purchase is above a spending limit. Approving it records your name and this reason.">
          <input value={exceptionReason} onChange={(e) => setExceptionReason(e.target.value)} />
        </Field>
      ) : (
        <p className="muted">This amount is kept aside from your credit until the fuel is filled or the indent ends.</p>
      )}
    </ConfirmDialog>
  );
  return { buttons, dialog };
}

export function Approvals() {
  const q = useQuery({ queryKey: ['approvals'], queryFn: () => api.get<IndentView[]>('/api/approvals/queue') });
  const approve = useApproveFlow();
  return (
    <div className="stack">
      <h1>Waiting for approval</h1>
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      {q.data && !q.data.length ? <Empty>Nothing to approve right now.</Empty> : null}
      <ul className="tickets">
        {q.data?.map((i) => (
          <Ticket
            key={i.id}
            t={{ ...indentTicket(i, <>Asked {dt(i.requestedAt)}</>), extra: i.note ? <span className="ticket-extra">Note: {i.note}</span> : undefined }}
            to={`/buyer/indents/${i.id}`}
            actions={approve.buttons(i)}
          />
        ))}
      </ul>
      {approve.dialog}
    </div>
  );
}
